import {
  AssistedAffiliationUploadError,
  getAssistedAffiliationDocumentsService,
  uploadAssistedAffiliationFileService,
  validateAssistedAffiliationFile,
} from "@/services/assistedAffiliation/uploadAssistedAffiliationFileService";
import type {
  AssistedAffiliationDocumentField,
  AssistedAffiliationDocumentReferences,
  AssistedAffiliationDocumentRequest,
  DraftDocumentPaths,
} from "@/services/assistedAffiliation/types";
import type {
  AssistedAffiliationFileName,
  AssistedAffiliationFiles,
  AssistedAffiliationForm,
  LegalDocumentFiles,
  SavedAssistedAffiliationFiles,
} from "./types";

interface DraftDocument {
  field: AssistedAffiliationFileName;
  uploadName: string;
  storedName: string;
  draftField?: AssistedAffiliationDocumentField;
}

const DOCUMENTS: DraftDocument[] = [
  { field: "frontIdentification", uploadName: "CEDULA_FRONT", storedName: "CEDULA_FRENTE" },
  { field: "backIdentification", uploadName: "CEDULA_BACK", storedName: "CEDULA_DORSO" },
  { field: "passportPhoto", uploadName: "PASSPORT", storedName: "PASAPORTE", draftField: "passport_image_url_file" },
  { field: "accountEvidence", uploadName: "BANK_CERTIFICATION", storedName: "CERTIFICACION_BANCARIA", draftField: "attachment" },
  { field: "rncRegistration", uploadName: "RNC_CERTIFICATION", storedName: "CERTIFICACION_RNC", draftField: "rnc_certification" },
  { field: "merchantRegistry", uploadName: "COMMERCIAL_REGISTER", storedName: "REGISTRO_COMERCIAL", draftField: "commercial_register" },
  { field: "managerDesignation", uploadName: "REPRESENTATIVE_ACT", storedName: "ACTA_REPRESENTATIVA", draftField: "representative_act" },
  { field: "socialStatutes", uploadName: "BYLAWS", storedName: "ESTATUTOS", draftField: "bylaws" },
];

export type DocumentUploadCache = Map<string, { file: File; path: string }>;

const getApplicableDocuments = (isForeign: boolean) => DOCUMENTS.filter(({ field }) => {
  if (isForeign) return field !== "frontIdentification" && field !== "backIdentification";
  return field !== "passportPhoto";
});

export const uploadDraftDocuments = async (
  form: AssistedAffiliationForm,
  files: AssistedAffiliationFiles,
  legalFiles: LegalDocumentFiles,
  cache: DocumentUploadCache
): Promise<DraftDocumentPaths> => {
  const selected = { ...files, ...legalFiles };
  const uploads = getApplicableDocuments(form.isForeignRepresentative).flatMap(document => {
    const value = selected[document.field];
    if (!value) return [];
    const selectedFiles = Array.isArray(value) ? value : [value];
    return selectedFiles.map((file, index) => ({
      file,
      name: index === 0 ? document.uploadName : `${document.uploadName}_${index + 1}`,
      draftField: index === 0 ? document.draftField : undefined,
    }));
  });

  // Validar el lote completo antes de iniciar cualquier petición.
  uploads.forEach(({ file }) => validateAssistedAffiliationFile(file));
  const paths: DraftDocumentPaths = {};
  for (const { name, file, draftField } of uploads) {
    const key = `${form.documentNumber.trim()}/${name}`;
    const previous = cache.get(key);
    let path: string;
    if (previous?.file === file) {
      path = previous.path;
    } else {
      const uploaded = await uploadAssistedAffiliationFileService(form.documentNumber, name, file);
      path = uploaded.path;
      cache.set(key, { file, path });
    }
    if (draftField) paths[draftField] = path;
  }
  return paths;
};

const getStoredFileName = (documentNumber: string, reference: string) => {
  let key = reference;
  if (reference.includes("://")) {
    key = decodeURIComponent(new URL(reference).pathname).replace(/^\//, "");
  }
  const prefix = `${documentNumber}-COMM/AAP/`;
  const fileName = key.slice(prefix.length);
  if (!key.startsWith(prefix) || fileName.includes("/") || !/\.(jpeg|png|pdf)$/i.test(fileName)) {
    throw new Error("Invalid document path");
  }
  return fileName;
};

export const getDraftDocuments = async (
  documentNumber: string,
  references: AssistedAffiliationDocumentReferences = {},
  isForeign = false
): Promise<Record<string, string>> => {
  try {
    const id = documentNumber.trim();
    if (!id) throw new Error("Missing document number");
    const requests: AssistedAffiliationDocumentRequest[] = [];

    for (const { uploadName, draftField } of getApplicableDocuments(isForeign)) {
      const reference = draftField ? references[draftField] : undefined;
      if (draftField && !reference) continue;
      // Sin ruta de cédula en el draft, se mantienen los formatos provisionales.
      const files = reference
        ? [getStoredFileName(id, reference)]
        : ["jpeg", "png", "pdf"].map(extension => `${uploadName}.${extension}`);
      requests.push({ name: uploadName, files, hasSavedPath: Boolean(reference) });
    }
    return getAssistedAffiliationDocumentsService(id, requests);
  } catch {
    throw new AssistedAffiliationUploadError("No se pudieron recuperar los archivos guardados. Vuelve a abrir la solicitud para reintentar.");
  }
};

export const mapSavedDraftDocuments = (documents: Record<string, string>): SavedAssistedAffiliationFiles => {
  const saved: SavedAssistedAffiliationFiles = {};
  for (const [name, url] of Object.entries(documents)) {
    const category = name.replace(/_\d+$/, "");
    const document = DOCUMENTS.find(({ uploadName, storedName }) =>
      uploadName === category || storedName === category
    );
    if (!document || typeof url !== "string") continue;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:") continue;
      const fileName = decodeURIComponent(parsed.pathname.split("/").pop() || name);
      const files = saved[document.field] ?? [];
      files.push({ name: fileName, url });
      saved[document.field] = files;
    } catch {
      // Un enlace inválido no cuenta como archivo guardado.
    }
  }
  return saved;
};
